import React from "react";
import PageShell from "@/components/PageShell";
import {
    COMPANY,
    DISCLAIMERS,
    PRODUCER,
    STATE_LICENSES,
    AGENCY,
    AGENCY_LICENSES,
} from "@/lib/compliance";

export default function LicensingPage() {
    return (
        <PageShell
            testId="page-licensing"
            kicker="Compliance"
            title="Licensing & Disclosures"
            seoDescription="Endurance Casualty Insurance Agency state licensing, lines of authority, NPN, and consumer-disclosure information for NY, NJ, PA, CT, NC, FL, and TX."
            lastUpdated="June 2026"
        >
            <p data-testid="licensing-last-reviewed" className="text-sm text-brand-muted">
                <strong className="text-brand-ivory">
                    Licensing Information Last Reviewed:
                </strong>{" "}
                June 2026
            </p>
            <p data-testid="licensing-verification-note">
                Licensing information is provided for consumer reference and
                should be verified through the National Insurance Producer
                Registry, the National Association of Insurance Commissioners,
                or the applicable state Department of Insurance. License
                status, lines of authority, appointments, and carrier
                availability may change. {COMPANY.shortName} periodically
                reviews this page, but users should verify current status
                before relying on licensing information.
            </p>
            <p>{DISCLAIMERS.stateScope}</p>
            <p>{DISCLAIMERS.notInsurer}</p>

            <h2>Agency</h2>
            <table>
                <tbody>
                    <tr>
                        <th>Legal Name</th>
                        <td>{COMPANY.fullLegalName}</td>
                    </tr>
                    <tr>
                        <th>Address</th>
                        <td>{COMPANY.address}</td>
                    </tr>
                    <tr>
                        <th>Phone</th>
                        <td>
                            <a href={`tel:${COMPANY.phoneTel}`}>
                                {COMPANY.phone}
                            </a>
                        </td>
                    </tr>
                    <tr>
                        <th>Email</th>
                        <td>
                            <a href={`mailto:${COMPANY.email}`}>
                                {COMPANY.email}
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <h2>Licensed Producer of Record</h2>
            <table>
                <tbody>
                    <tr>
                        <th>Producer Name</th>
                        <td>{PRODUCER.name}</td>
                    </tr>
                    <tr>
                        <th>National Producer Number (NPN)</th>
                        <td>
                            <a
                                href={`https://nipr.com/help/look-up-your-license?npn=${PRODUCER.npn}`}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {PRODUCER.npn}
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p>
                Consumers may independently verify the producer&apos;s status,
                appointments, and license history through the National
                Insurance Producer Registry using the NPN above.
            </p>

            <h2>State Licensing</h2>
            <p>
                {COMPANY.shortName} and its licensed producer of record are
                authorized to transact insurance only in the states listed
                below, and only for the lines of authority shown. All numbers
                are recorded with the National Insurance Producer Registry
                (NIPR) and may be verified directly with each state&apos;s
                Department of Insurance.
            </p>
            <table data-testid="state-licensing-table">
                <thead>
                    <tr>
                        <th>State</th>
                        <th>Lines of Authority</th>
                        <th>License Number</th>
                    </tr>
                </thead>
                <tbody>
                    {STATE_LICENSES.map((row, idx) => (
                        <tr key={`${row.code}-${row.number}-${idx}`}>
                            <td>
                                <abbr title={row.state}>{row.code}</abbr>
                                {" — "}
                                {row.state}
                            </td>
                            <td>{row.lines}</td>
                            <td>
                                <code>{row.number}</code>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p>
                <em>
                    Listings outside these states should not be inferred from
                    this Site. If your state is not listed, please contact us
                    before relying on any information here for licensure or
                    insurance purposes.
                </em>
            </p>

            <h2>Agency Licensing</h2>
            <p>
                In addition to the individual producer-of-record licenses
                above, {AGENCY.legalName} holds its own corporate-entity
                licenses with the National Insurance Producer Registry under
                the agency NPN below.
            </p>
            <table>
                <tbody>
                    <tr>
                        <th>Agency Legal Name</th>
                        <td>{AGENCY.legalName}</td>
                    </tr>
                    <tr>
                        <th>Agency National Producer Number (NPN)</th>
                        <td>
                            <a
                                href={`https://nipr.com/help/look-up-your-license?npn=${AGENCY.npn}`}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {AGENCY.npn}
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table data-testid="agency-licensing-table">
                <thead>
                    <tr>
                        <th>State</th>
                        <th>Lines of Authority</th>
                        <th>License Number</th>
                    </tr>
                </thead>
                <tbody>
                    {AGENCY_LICENSES.map((row, idx) => (
                        <tr key={`agency-${row.code}-${row.number}-${idx}`}>
                            <td>
                                <abbr title={row.state}>{row.code}</abbr>
                                {" — "}
                                {row.state}
                            </td>
                            <td>{row.lines}</td>
                            <td>
                                <code>{row.number}</code>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>


            <h2>Carrier Appointments and Disclosures</h2>
            <p>
                We do not list specific insurer names or carrier logos on the
                Site unless we have written authorization to do so and a current
                appointment with that insurer in the relevant state. Available
                carriers may change at any time. Quotes obtained through us are
                non-binding and subject to underwriting, eligibility, and
                policy terms.
            </p>

            <h2>Florida-Specific Disclosure</h2>
            <p>{DISCLAIMERS.floridaSpecific}</p>

            <h2>Texas-Specific Disclosure</h2>
            <p>{DISCLAIMERS.texasSpecific}</p>

            <h2>Verifying Our License</h2>
            <p>
                Consumers may verify our agency and producer licenses through
                the National Association of Insurance Commissioners&apos; (NAIC)
                State Based Systems portal at{" "}
                <a
                    href="https://nipr.com/help/look-up-your-license"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    nipr.com
                </a>
                {" "}or directly with the Department of Insurance in your state.
            </p>
        </PageShell>
    );
}
